import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import type {
  ArShapeFeature,
  ArShapesResponse,
  BenchLevel,
  Blast,
  BlockFeature,
  BlocksResponse,
  DrillDomainsResponse,
  DrilledDataResponse,
  DrilledHole,
  DrillHole,
  DrillHolesResponse,
} from '~api/services';
import { BASE_COLOURS } from '~config/colours';
import type { ColourScale } from '~utils';
import type { CollarOrToe } from '../components/CollarOrToeSelect';
import type { NeighbouringHoleColourByMode } from '../components/NeighbouringHoleColourBySelect';
import type { LayerVisibilities, QueryState } from '../hooks';
import { useBlastDesignContextLayers, useSelectionState } from '../hooks';
import {
  createBlockColourScale,
  createDrilledHoleColourScale,
  createNeighbouringHoleColourScale,
  createNeighbouringHoleContinuousColourScale,
} from '../utils';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type PlannedOrActual = 'planned' | 'actual';

type NeighbouringContinuousColourMode = Exclude<NeighbouringHoleColourByMode, 'blast' | 'drill_model'>;

const CONTINUOUS_MODE_CONFIG: Record<
  NeighbouringContinuousColourMode,
  {
    property: keyof DrillHole;
    formatLabel: (value: number) => string;
    baseColour: (typeof BASE_COLOURS)[keyof typeof BASE_COLOURS];
  }
> = {
  rl: { property: 'rl', formatLabel: (value) => value.toFixed(0), baseColour: BASE_COLOURS.TEAL },
  diameter: { property: 'diameter', formatLabel: (value) => value.toFixed(1), baseColour: BASE_COLOURS.ORANGE },
  depth: { property: 'depth', formatLabel: (value) => value.toFixed(1), baseColour: BASE_COLOURS.BLUE },
  bearing: { property: 'bearing', formatLabel: (value) => value.toFixed(0), baseColour: BASE_COLOURS.PURPLE },
  dip: { property: 'dip', formatLabel: (value) => value.toFixed(0), baseColour: BASE_COLOURS.RED },
};

type BlastDesignContextValue = {
  /** Blast data from the route loader */
  blast: Blast;

  /** Query results */
  haveBlocks: boolean;
  haveDrilledHoles: boolean;
  haveDrillDomains: boolean;
  haveNeighbouringDrillHoles: boolean;
  haveArShapes: boolean;
  blocksQuery: QueryState<BlocksResponse>;
  drilledDataQuery: QueryState<DrilledDataResponse>;
  drillDomainsQuery: QueryState<DrillDomainsResponse>;
  neighbouringDrillHolesQuery: QueryState<DrillHolesResponse>;
  arShapesQuery: QueryState<ArShapesResponse>;

  /** Filtered blocks for the selected bench level */
  blocks: BlockFeature[] | undefined;
  /** Colour scale derived from blocks and selected property */
  blockColourScale: ColourScale | undefined;
  /** Colour scale derived from drilled holes and selected property */
  drilledHoleColourScale: ColourScale | undefined;
  /** Default colour scale for neighbouring drilled holes (grouped by pattern_name) */
  neighbouringHoleColourScale: ColourScale | undefined;
  /** Neighbouring drill holes filtered by selected pattern */
  filteredNeighbouringDrillHoles: DrillHole[] | undefined;
  /** Unique neighbouring pattern names sorted alphabetically */
  patternNames: string[];
  /** Valid RL values extracted from neighbouring holes */
  validRLs: number[];
  /** RL values mapped by pattern name for lookup */
  patternRlsByName: Map<string, number[]>;
  /** RL bounds derived from valid RL values */
  rlBounds: [number, number] | null;

  /** Bench level state */
  benchLevel: BenchLevel;
  setBenchLevel: (level: BenchLevel) => void;
  /** Block model name setter (triggers blocks re-fetch) */
  setSelectedModelName: (name: string) => void;
  /** Block property for colouring */
  selectedBlockProperty: string;
  setSelectedBlockProperty: (property: string) => void;
  /** Whether block colour labels are visible on the canvas */
  showBlockLabels: boolean;
  setShowBlockLabels: (show: boolean) => void;
  /** Drilled hole property for colouring */
  selectedHoleProperty: string;
  setSelectedHoleProperty: (property: string) => void;
  /** Planned vs actual drill hole coordinates */
  plannedOrActual: PlannedOrActual;
  setPlannedOrActual: (value: PlannedOrActual) => void;
  /** Per-pattern visibility toggle map for neighbouring drill holes */
  patternVisibility: Record<string, boolean>;
  setPatternVisibility: (patternName: string, isVisible: boolean) => void;
  /** Render neighbouring holes at collar or toe positions */
  collarOrToe: CollarOrToe;
  setCollarOrToe: (value: CollarOrToe) => void;
  /** Neighbouring hole colour mode selector */
  colourByMode: NeighbouringHoleColourByMode;
  setColourByMode: (mode: NeighbouringHoleColourByMode) => void;
  /** RL range filter for neighbouring drill holes, null means no filter */
  rlRange: [number, number] | null;
  setRlRange: (range: [number, number] | null) => void;
  /** Include holes that belong to the current blast in neighbouring-hole rendering */
  includeCurrentBlastHoles: boolean;
  setIncludeCurrentBlastHoles: (include: boolean) => void;

  /** Selection state */
  selectedBlock: BlockFeature | null;
  selectedBlockId: string | null;
  blockDrawerOpen: boolean;

  selectedHole: DrilledHole | null;
  selectedHoleId: string | null;
  holeDrawerOpen: boolean;

  selectedDrillHole: DrillHole | null;
  selectedDrillHoleId: string | null;
  drillHoleDrawerOpen: boolean;

  selectedArShape: ArShapeFeature | null;
  selectedArShapeId: string | null;
  arShapeDrawerOpen: boolean;

  handleBlockClick: (block: BlockFeature) => void;
  handleHoleClick: (hole: DrilledHole) => void;
  handleDrillHoleClick: (drillHole: DrillHole) => void;
  handleArShapeClick: (shape: ArShapeFeature) => void;
  handleDrawerClose: () => void;

  /** Layer visibility controls */
  layers: LayerVisibilities;
};

/* -------------------------------------------------------------------------- */
/*                                   Context                                  */
/* -------------------------------------------------------------------------- */

const BlastDesignContext = createContext<BlastDesignContextValue | null>(null);

/**
 * Consume the blast design context page context.
 * Must be used within a BlastDesignContextProvider.
 *
 * @example
 * ```tsx
 * const {
 *   blast,
 *   blocksQuery,
 *   drilledDataQuery,
 *   benchLevel,
 *   setBenchLevel,
 *   selectedBlockProperty,
 *   setSelectedBlockProperty,
 *   selectedHoleProperty,
 *   setSelectedHoleProperty,
 *   plannedOrActual,
 *   setPlannedOrActual,
 *   selectedBlock,
 *   handleBlockClick,
 *   layers,
 * } = useBlastDesignContext();
 * ```
 */
const useBlastDesignContext = (): BlastDesignContextValue => {
  const ctx = useContext(BlastDesignContext);
  if (!ctx) {
    throw new Error('useBlastDesignContext must be used within a BlastDesignContextProvider');
  }
  return ctx;
};

/* -------------------------------------------------------------------------- */
/*                                  Provider                                  */
/* -------------------------------------------------------------------------- */

/**
 * Provides all shared state, queries, and derived data for the Blast Design Context page.
 * Children consume via useBlastDesignContext() avoid prop drilling and ensure consistent data access across components.
 *
 * @example
 * ```tsx
 * <BlastDesignContextProvider>
 *   <BlastDesignContextPage />
 * </BlastDesignContextProvider>
 * ```
 */
const BlastDesignContextProvider = ({ children }: { children: ReactNode }) => {
  // UI state
  const [selectedModelName, setSelectedModelName] = useState<string>();
  const [benchLevel, setBenchLevel] = useState<BenchLevel>('middle');
  const [selectedBlockProperty, setSelectedBlockProperty] = useState('hardness');
  const [selectedHoleProperty, setSelectedHoleProperty] = useState('hardness');
  const [plannedOrActual, setPlannedOrActual] = useState<PlannedOrActual>('planned');
  const [patternVisibility, setPatternVisibility] = useState<Record<string, boolean>>({});
  const [collarOrToe, setCollarOrToe] = useState<CollarOrToe>('toe');
  const [colourByMode, setColourByMode] = useState<NeighbouringHoleColourByMode>('blast');
  const [rlRange, setRlRange] = useState<[number, number] | null>(null);
  const [includeCurrentBlastHoles, setIncludeCurrentBlastHoles] = useState(false);
  const [showBlockLabels, setShowBlockLabels] = useState(true);

  // Queries, layer visibility, presence flags, and auto-hide effects
  const {
    blast,
    blocksQuery,
    drilledDataQuery,
    drillDomainsQuery,
    neighbouringDrillHolesQuery,
    arShapesQuery,
    layers,
    haveBlocks,
    haveDrillDomains,
    haveDrilledHoles,
    haveNeighbouringDrillHoles,
    haveArShapes,
  } = useBlastDesignContextLayers({ selectedModelName });

  // Selection state
  const {
    selectedBlock,
    selectedBlockId,
    blockDrawerOpen,

    selectedHole,
    selectedHoleId,
    holeDrawerOpen,

    selectedDrillHole,
    selectedDrillHoleId,
    drillHoleDrawerOpen,

    selectedArShape,
    selectedArShapeId,
    arShapeDrawerOpen,

    handleBlockClick,
    handleHoleClick,
    handleArShapeClick,
    handleDrillHoleClick,
    handleDrawerClose,
  } = useSelectionState();

  // Derived data
  const blocks = useMemo(() => {
    if (!blocksQuery.data?.blocks) return;
    return blocksQuery.data.blocks.filter((b) => b.properties.bench_levels.includes(benchLevel));
  }, [blocksQuery.data?.blocks, benchLevel]);

  const blockColourScale = useMemo(
    () =>
      blocksQuery.data?.blocks ? createBlockColourScale(blocksQuery.data.blocks, selectedBlockProperty) : undefined,
    [blocksQuery.data?.blocks, selectedBlockProperty],
  );

  const drilledHoleColourScale = useMemo(
    () =>
      drilledDataQuery.data?.drilled_data
        ? createDrilledHoleColourScale(drilledDataQuery.data.drilled_data, selectedHoleProperty)
        : undefined,
    [drilledDataQuery.data?.drilled_data, selectedHoleProperty],
  );

  const validNeighbouringDrillHoles = useMemo(
    () => neighbouringDrillHolesQuery.data?.drill_holes?.filter((hole) => hole.valid === 'valid'),
    [neighbouringDrillHolesQuery.data?.drill_holes],
  );

  const neighbouringHoleColourScale = useMemo(() => {
    if (!validNeighbouringDrillHoles?.length) return undefined;

    if (colourByMode === 'blast') {
      return createNeighbouringHoleColourScale(validNeighbouringDrillHoles, 'pattern_name');
    }

    if (colourByMode === 'drill_model') {
      return createNeighbouringHoleColourScale(validNeighbouringDrillHoles, 'drill_model');
    }

    const { property, formatLabel, baseColour } = CONTINUOUS_MODE_CONFIG[colourByMode];
    return createNeighbouringHoleContinuousColourScale(validNeighbouringDrillHoles, property, formatLabel, baseColour);
  }, [validNeighbouringDrillHoles, colourByMode]);

  const patternNames = useMemo(
    () => [...new Set(validNeighbouringDrillHoles?.map((hole) => hole.pattern_name) ?? [])].sort(),
    [validNeighbouringDrillHoles],
  );

  const validRLs = useMemo(
    () => [...new Set(validNeighbouringDrillHoles?.map((hole) => hole.rl) ?? [])].sort((a, b) => a - b),
    [validNeighbouringDrillHoles],
  );

  const patternRlsByName = useMemo(() => {
    const sets = new Map<string, Set<number>>();

    for (const hole of validNeighbouringDrillHoles ?? []) {
      const rlSet = sets.get(hole.pattern_name) ?? new Set<number>();
      rlSet.add(hole.rl);
      sets.set(hole.pattern_name, rlSet);
    }

    return new Map([...sets].map(([name, rlSet]) => [name, [...rlSet]]));
  }, [validNeighbouringDrillHoles]);

  const rlBounds = useMemo<[number, number] | null>(
    () => (validRLs.length ? [validRLs[0], validRLs[validRLs.length - 1]] : null),
    [validRLs],
  );

  const handlePatternVisibility = useCallback((patternName: string, isVisible: boolean) => {
    setPatternVisibility((previousVisibility) => ({
      ...previousVisibility,
      [patternName]: isVisible,
    }));
  }, []);

  const filteredNeighbouringDrillHoles = useMemo(() => {
    if (!validNeighbouringDrillHoles) return undefined;

    let holes = validNeighbouringDrillHoles;

    if (!includeCurrentBlastHoles) {
      holes = holes.filter((hole) => hole.pattern_name !== blast.name);
    }

    if (rlRange) {
      // Filter by RL range when set
      holes = holes.filter(
        (hole) =>
          (includeCurrentBlastHoles && hole.pattern_name === blast.name) ||
          (hole.rl >= rlRange[0] && hole.rl <= rlRange[1]),
      );
    }

    holes = holes.filter((hole) => patternVisibility[hole.pattern_name] ?? true);

    return holes;
  }, [validNeighbouringDrillHoles, includeCurrentBlastHoles, blast.name, rlRange, patternVisibility]);

  const value: BlastDesignContextValue = useMemo(
    () => ({
      blast,
      // Blocks Model presence flags and queries
      haveBlocks,
      blocksQuery,
      // Drill Domains presence flags and queries
      haveDrillDomains,
      drillDomainsQuery,
      // Drilled holes presence flag and query
      haveDrilledHoles,
      drilledDataQuery,
      // Neighbouring drill holes presence flag and query
      haveNeighbouringDrillHoles,
      neighbouringDrillHolesQuery,
      // AR Shapes presence flag and query
      haveArShapes,
      arShapesQuery,
      // Derived data
      blocks,
      blockColourScale,
      drilledHoleColourScale,
      neighbouringHoleColourScale,
      filteredNeighbouringDrillHoles,
      patternNames,
      validRLs,
      patternRlsByName,
      rlBounds,
      // UI state and setters
      benchLevel,
      setBenchLevel,
      setSelectedModelName,
      selectedBlockProperty,
      setSelectedBlockProperty,
      showBlockLabels,
      setShowBlockLabels,
      selectedHoleProperty,
      setSelectedHoleProperty,
      plannedOrActual,
      setPlannedOrActual,
      patternVisibility,
      setPatternVisibility: handlePatternVisibility,
      collarOrToe,
      setCollarOrToe,
      colourByMode,
      setColourByMode,
      rlRange,
      setRlRange,
      includeCurrentBlastHoles,
      setIncludeCurrentBlastHoles,
      // Selection state and handlers
      selectedBlock,
      selectedBlockId,
      blockDrawerOpen,
      selectedHole,
      selectedHoleId,
      holeDrawerOpen,
      selectedDrillHole,
      selectedDrillHoleId,
      drillHoleDrawerOpen,
      selectedArShape,
      selectedArShapeId,
      arShapeDrawerOpen,
      handleBlockClick,
      handleHoleClick,
      handleDrillHoleClick,
      handleArShapeClick,
      handleDrawerClose,
      // Layer visibility controls
      layers,
    }),
    [
      blast,
      haveBlocks,
      blocksQuery,
      haveDrillDomains,
      drillDomainsQuery,
      haveDrilledHoles,
      drilledDataQuery,
      haveNeighbouringDrillHoles,
      neighbouringDrillHolesQuery,
      haveArShapes,
      arShapesQuery,
      blocks,
      blockColourScale,
      drilledHoleColourScale,
      neighbouringHoleColourScale,
      filteredNeighbouringDrillHoles,
      patternNames,
      validRLs,
      patternRlsByName,
      rlBounds,
      benchLevel,
      selectedBlockProperty,
      showBlockLabels,
      selectedHoleProperty,
      plannedOrActual,
      patternVisibility,
      handlePatternVisibility,
      collarOrToe,
      colourByMode,
      rlRange,
      includeCurrentBlastHoles,
      selectedBlock,
      selectedBlockId,
      blockDrawerOpen,
      selectedHole,
      selectedHoleId,
      holeDrawerOpen,
      selectedDrillHole,
      selectedDrillHoleId,
      drillHoleDrawerOpen,
      selectedArShape,
      selectedArShapeId,
      arShapeDrawerOpen,
      handleBlockClick,
      handleHoleClick,
      handleDrillHoleClick,
      handleArShapeClick,
      handleDrawerClose,
      layers,
    ],
  );

  return <BlastDesignContext.Provider value={value}>{children}</BlastDesignContext.Provider>;
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export type { CollarOrToe, PlannedOrActual };
export { BlastDesignContextProvider, useBlastDesignContext };
